import type { ErrorEvent } from '@sentry/vue';

/**
 * Whether `err` is the AbortError a browser rejects a pending play() with when a later pause(), a
 * source change or removal from the document interrupts it. That is the expected outcome of
 * stopping playback quickly, not a failure.
 */
export function isPlayInterruptedError(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * Start playback. Resolves `true` once playing, or `false` when an interruption (see
 * {@link isPlayInterruptedError}) superseded it. Real failures — NotAllowedError,
 * NotSupportedError — still reject so the caller can surface them.
 */
export function playMedia(el: HTMLMediaElement): Promise<boolean> {
    return el.play().then(
        () => true,
        err => {
            if (isPlayInterruptedError(err)) return false;
            throw err;
        }
    );
}

const PLAY_INTERRUPTED_MESSAGE = 'The play() request was interrupted';

/**
 * Sentry `beforeSend` filter for an interrupted-play AbortError that reached the global
 * unhandled-rejection handler (e.g. a play() inside a third-party library).
 */
export function excludePlayInterruptedErrors(event: ErrorEvent): ErrorEvent | null {
    const isPlayInterrupted = event.exception?.values?.some(exception => exception.value?.includes(PLAY_INTERRUPTED_MESSAGE));
    return isPlayInterrupted ? null : event;
}
